import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, ShoppingBasket, CarFront, HeartHandshake, HandHeart, HandHelping, Flag, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, CATEGORIES, STATUS_LABEL } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { StarRating } from "@/components/StarRating";
import { TrustBadge } from "@/components/TrustBadge";
import { timeAgo } from "@/components/PostCard";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const CAT_ICON = { groceries: ShoppingBasket, transport: CarFront, companionship: HeartHandshake };
const STATUS_STYLE = {
  open: "bg-forest-700 text-white", connected: "bg-gold text-forest-900",
  in_progress: "bg-terracotta text-white", completed: "bg-stone-200 text-forest-700",
};

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [author, setAuthor] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [reportOpen, setReportOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/posts/${id}`);
      setPost(data);
      const u = await api.get(`/users/${data.author_id}`);
      setAuthor(u.data.user);
    } catch (e) { toast.error(apiError(e)); navigate("/app"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isMine = post && user && post.author_id === user.user_id;
  const cats = post ? (post.categories && post.categories.length ? post.categories : [post.category]) : [];
  const Icon = post ? CAT_ICON[cats[0]] || HeartHandshake : HeartHandshake;

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${id}/connect`, { text: message });
      toast.success("Connected! Start chatting.");
      navigate(`/app/messages/${data.connection_id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const changeStatus = async (status) => {
    try { await api.patch(`/posts/${id}/status`, { status }); setPost({ ...post, status }); toast.success("Status updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async () => {
    try { await api.delete(`/posts/${id}`); toast.success("Post deleted"); navigate("/app"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const report = async () => {
    if (!reason.trim()) return toast.error("Please tell us what's wrong");
    try { await api.post("/reports", { target_type: "post", target_id: id, reason }); setReportOpen(false); setReason(""); toast.success("Report sent to our team"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!post) return <div className="h-64 rounded-2xl bg-white/60 border-2 border-border animate-pulse" />;
  const isOffer = post.kind === "offer";

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-forest-700 font-semibold mb-4" data-testid="back-btn">
        <ArrowLeft size={20} strokeWidth={2.5} /> Back
      </button>

      <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold ${isOffer ? "bg-forest-700 text-white" : "bg-terracotta text-white"}`}>
            {isOffer ? <HandHeart size={16} strokeWidth={2.5} /> : <HandHelping size={16} strokeWidth={2.5} />}
            {isOffer ? "Offering help" : "Needs help"}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLE[post.status]}`}>{STATUS_LABEL[post.status]}</span>
          <span className="text-forest-500 text-sm ml-auto">{timeAgo(post.created_at)}</span>
        </div>

        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-cream border-2 border-border flex items-center justify-center shrink-0">
            <Icon size={28} strokeWidth={2.5} className="text-forest-700" />
          </div>
          <div>
            <h1 className="font-head text-2xl sm:text-3xl font-bold text-forest-900 leading-snug">{post.title}</h1>
            <div className="flex flex-wrap gap-2 mt-2">
              {cats.map((c) => (
                <span key={c} className="px-3 py-1 rounded-full bg-cream border-2 border-border text-forest-700 font-semibold text-sm">{CATEGORIES[c]?.label}</span>
              ))}
            </div>
          </div>
        </div>

        <p className="text-lg text-forest-700 leading-relaxed mt-5 whitespace-pre-wrap">{post.description}</p>
        {post.address && (
          <p className="flex items-center gap-2 text-forest-700 mt-4 font-medium">
            <MapPin size={20} strokeWidth={2.5} className="text-terracotta" /> {post.address}
            {post.distance_km != null && <span className="text-forest-500">· {post.distance_km} km away</span>}
          </p>
        )}

        {/* Author */}
        <button onClick={() => navigate(`/app/members/${post.author_id}`)} data-testid="view-author"
          className="w-full flex items-center gap-3 mt-6 pt-6 border-t border-border text-left hover:bg-cream rounded-xl -mx-2 px-2 py-2 transition-colors">
          <Avatar user={author || { name: post.author_name, picture: post.author_picture }} size={52} />
          <div>
            <p className="font-bold text-forest-900 text-lg">{post.author_name}</p>
            {author && <div className="flex items-center gap-2 flex-wrap"><StarRating value={author.rating_avg} count={author.rating_count} size={16} /><TrustBadge level={author.trust_level} size="sm" /></div>}
          </div>
          <span className="ml-auto text-terracotta font-semibold">View profile →</span>
        </button>
      </div>

      {/* Actions */}
      {isMine ? (
        <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 mt-5">
          <h3 className="font-bold text-forest-900 text-lg mb-3">Manage your post</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {["open", "in_progress", "completed"].map((s) => (
              <button key={s} onClick={() => changeStatus(s)} data-testid={`status-${s}`}
                className={`px-4 py-2.5 rounded-full font-semibold border-2 transition-colors ${post.status === s ? "bg-forest-900 text-white border-forest-900" : "bg-white text-forest-700 border-border"}`}>
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
          <button onClick={remove} data-testid="delete-post"
            className="flex items-center gap-2 text-destructive font-semibold hover:underline">
            <Trash2 size={18} strokeWidth={2.5} /> Delete post
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 mt-5">
          <h3 className="font-bold text-forest-900 text-xl mb-3">Reach out to {post.author_name.split(" ")[0]}</h3>
          <textarea data-testid="connect-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
            placeholder={`Say hello and how you'd like to ${isOffer ? "get help" : "help"}…`}
            className="w-full text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-cream resize-none mb-3" />
          <button onClick={connect} disabled={busy} data-testid="connect-btn"
            className="w-full min-h-[56px] rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-transform active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2">
            <Send size={20} strokeWidth={2.5} /> {busy ? "Connecting…" : "Connect & send message"}
          </button>
        </div>
      )}

      {!isMine && (
        <Dialog open={reportOpen} onOpenChange={setReportOpen}>
          <DialogTrigger asChild>
            <button data-testid="report-btn" className="flex items-center gap-2 text-forest-500 font-semibold mt-4 mx-auto hover:text-destructive transition-colors">
              <Flag size={18} strokeWidth={2.5} /> Report this post
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white border-2 border-border rounded-2xl">
            <DialogHeader><DialogTitle className="font-head text-2xl text-forest-900">Report post</DialogTitle></DialogHeader>
            <textarea data-testid="report-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
              placeholder="What's the problem?" className="w-full text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream resize-none" />
            <DialogFooter>
              <button onClick={report} data-testid="report-submit" className="min-h-[52px] px-6 rounded-full bg-destructive text-white font-bold hover:opacity-90">Send report</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
